import React, { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography, Tooltip, useTheme } from "@mui/material";
import { tokens } from "../../assets/theme";
import { fetchVersion } from "../../fetchers/version";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

export default function VersionNumber(props) {
  const { reloadOnTimer } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const intl = useIntl();

  const prevVersionRef = useRef(null);

  const reloadWithCacheBust = (reason) => {
    localStorage.setItem("appLastForcedReloadAt", new Date().toISOString());
    localStorage.setItem("appLastForcedReloadReason", reason);

    const url = new URL(window.location.href);
    // Separat param så vi inte bråkar med _v (version) om den finns
    url.searchParams.set("_r", Date.now().toString());
    window.location.replace(url.toString());
  };

  // Log last page load on every mount (manual reload or first visit)
  useEffect(() => {
    localStorage.setItem("appLastPageLoadAt", new Date().toISOString());
  }, []);

  // Force reload if the page has been open more than 6 hours
  useEffect(() => {
	// No timer-based reload if prop is not set, when the feature query is ready the effect will re-run
	if (reloadOnTimer == null) return;

    const loadIso = localStorage.getItem("appLastPageLoadAt");
    const loadAt = loadIso ? new Date(loadIso).getTime() : Date.now();

    const checkAndReloadIfNeeded = () => {
      const ageMs = Date.now() - loadAt;
      if (ageMs >= SIX_HOURS_MS && reloadOnTimer) {
        reloadWithCacheBust("open-more-than-6-hours");
      }
    };

    // Schedule one-shot timeout for “remaining time”
    const remainingMs = SIX_HOURS_MS - (Date.now() - loadAt);
    const timeoutId = window.setTimeout(
      checkAndReloadIfNeeded,
      Math.max(0, remainingMs)
    );

    // Also check on focus/visibility changes (sleep/hibernation-safe)
    const onFocus = () => checkAndReloadIfNeeded();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkAndReloadIfNeeded();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Immediate check in case clock changed or stored value is old
    checkAndReloadIfNeeded();

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [reloadOnTimer]);

  const { data, isSuccess } = useQuery(["version"], () => fetchVersion(), {
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    const current = data?.version;
    if (!isSuccess || !current) return;

    const stored = localStorage.getItem("appVersion");

    if (prevVersionRef.current === null) {
      prevVersionRef.current = stored ?? current;
      if (!stored) localStorage.setItem("appVersion", current);
      return;
    }

    // When backend version changes -> mark version change and cache-bust
    if (prevVersionRef.current !== current || stored !== current) {
      localStorage.setItem("appVersion", current);
      localStorage.setItem("appLastVersionChangeAt", new Date().toISOString());

      const url = new URL(window.location.href);
      url.searchParams.set("_v", current);
      window.location.replace(url.toString());
    }
  }, [isSuccess, data?.version]);

  // Pick the latest of version change and page load
  const lastVersionChangeAt = localStorage.getItem("appLastVersionChangeAt");
  const lastPageLoadAt = localStorage.getItem("appLastPageLoadAt");

  const dates = [lastVersionChangeAt, lastPageLoadAt]
    .filter(Boolean)
    .map((v) => new Date(v));
  const latestIso = dates.length
    ? dates.sort((a, b) => b.getTime() - a.getTime())[0]
    : null;

  const latestText = latestIso
    ? `${intl.formatDate(latestIso, { dateStyle: "short" })} ${intl.formatTime(
        latestIso,
        { timeStyle: "medium" }
      )}`
    : intl.formatMessage(messages.noLoadsYet);

  const tooltipTitle = (
    <Typography
      component="span"
      sx={{ whiteSpace: "pre-line", fontSize: "0.7rem" }}
    >
      <FormattedMessage {...messages.lastUpdated} /> {latestText}
    </Typography>
  );

  return (
    <>
      {isSuccess && data && (
        <Tooltip
          title={tooltipTitle}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: theme.palette.mode === "dark" ? "grey.800" : "grey.100",
                color: theme.palette.mode === "dark" ? "grey.100" : "grey.900",
                border: 1,
                borderColor: theme.palette.mode === "dark" ? "grey.700" : "grey.300",
                boxShadow: 3,
                px: 1.5,
                py: 1,
              },
            },
            arrow: {
              sx: {
                color: theme.palette.mode === "dark" ? "grey.800" : "grey.100",
              },
            },
          }}
        >
          <Typography
            variant="h6"
            color={colors.gray[200]}
            sx={{ m: "0px 0px 10px 0px", flex: 1, cursor: "default" }}
          >
            Version: {data.version}
          </Typography>
        </Tooltip>
      )}
    </>
  );
}